import type { Language } from '@seek/melways-sites';

import JobApplication, { type IJobApplication } from './../JobApplication';
import jobApplicationService from '../../../services/jobApplicationService';
import { STATE_ERROR, STATE_LOADED } from '../../state';
import { logError } from '../../../utils/logError';
import type { Prospect } from 'src/graphql/types';
import type { ApplicationFragment } from 'src/graphql/application.hirer.graphql.generated';
import {
  mapApiResponseToStore,
  mapApplicationResponseToStore,
} from './mapApiResponseToStore';
import type { Bucket, SearchFilters } from '../types';
import { PER_PAGE } from '../../../constants/numPerPage';

import type {
  FilterCounts,
  BucketStatusCounts,
} from '../../../graphql/fetchProspectsWithPaginationByJobId';

interface BucketStatsStore {
  state: string;
  bucketStats: BucketStatusCounts;
}

export interface JobApplicationListResult {
  jobApplications: IJobApplication[];
  state: string;
  preFilterTotal: number;
  total: number;
  filterCounts: FilterCounts;
  bucketStatsStore: BucketStatsStore;
}

export const getJobApplicationList = async (
  jobId: number,
  searchText: string,
  language: Language,
  displayCountry: string,
  bucket: Bucket,
  sortBy: string, // TODO_TYPES
  pageNumber: number = 1,
  selectedJobApplicationIds: string[] = [],
  filters: SearchFilters,
): Promise<JobApplicationListResult | 'UNAUTHENTICATED'> => {
  try {
    const jobApplicationServiceFetchResult = await jobApplicationService.fetch(
      jobId,
      searchText,
      language,
      displayCountry,
      bucket as any,
      sortBy,
      pageNumber,
      filters,
    );
    const offset = (pageNumber - 1) * PER_PAGE;

    if (jobApplicationServiceFetchResult.source === 'cm-graph') {
      const jobApplications = jobApplicationServiceFetchResult.data.nodes.map(
        (jobApplication: Prospect, index: number) =>
          JobApplication.create({
            ...mapApiResponseToStore(jobApplication),
            positionInList: offset + index + 1,
            selected: selectedJobApplicationIds.includes(jobApplication.id),
          }),
      );

      return {
        jobApplications,
        preFilterTotal:
          jobApplicationServiceFetchResult.data.pageInfo?.preFilterTotal ?? 0,
        total: jobApplicationServiceFetchResult.data.pageInfo?.total ?? 0,
        state: STATE_LOADED,
        filterCounts: jobApplicationServiceFetchResult.data.filterCounts ?? {},
        bucketStatsStore: {
          state: STATE_LOADED,
          bucketStats: jobApplicationServiceFetchResult.data.bucketCounts ?? {},
        },
      };
    }

    if (jobApplicationServiceFetchResult.data === 'redirect') {
      return {
        jobApplications: [],
        preFilterTotal: 0,
        total: 0,
        state: STATE_LOADED,
        filterCounts: {},
        bucketStatsStore: {
          state: STATE_LOADED,
          bucketStats: {},
        },
      };
    }

    const jobApplications = jobApplicationServiceFetchResult.data.result.map(
      (jobApplication: ApplicationFragment, index: number) =>
        JobApplication.create(
          mapApplicationResponseToStore(
            jobApplication,
            offset + index + 1,
            selectedJobApplicationIds.includes(
              jobApplication.adcentreProspectId.toString(),
            ),
          ),
        ),
    );
    return {
      jobApplications,
      preFilterTotal:
        jobApplicationServiceFetchResult.data.pageInfo?.preFilterTotal ?? 0,
      total: jobApplicationServiceFetchResult.data.pageInfo?.total ?? 0,
      state: STATE_LOADED,
      filterCounts: {
        rrFilterCounts:
          jobApplicationServiceFetchResult.data.filterCounts.questionnaireFilterCounts.map(
            ({ id, count }) => ({ id, count }),
          ),
      },
      bucketStatsStore: {
        state: STATE_LOADED,
        bucketStats: jobApplicationServiceFetchResult.data.statusFolderCounts,
      },
    };
  } catch (error: any) {
    logError(
      {
        errorCode: 'GET_JOB_APPLICATION',
        description: 'Error attempting: jobApplicationService.fetch',
      },
      {
        error,
      },
    );
    if (error.message === 'UNAUTHENTICATED') {
      return 'UNAUTHENTICATED';
    }

    return {
      jobApplications: [],
      preFilterTotal: 0,
      total: 0,
      state: STATE_ERROR,
      filterCounts: {},
      bucketStatsStore: {
        state: STATE_LOADED,
        bucketStats: {},
      },
    };
  }
};
